<template>
  <d-container fluid class="main-content-container px-0">
    <h4 class="mb-3 pb-3 border-bottom d-flex">
      {{ $t('customer.contact.notifications') }}

      <d-button
        theme="success"
        @click.prevent="addNotification()"
        class="ml-auto">
        <i class="material-icons">add</i>
        {{ $t('notifications.addNewNotification') }}
      </d-button>
    </h4>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('notifications.description') }}</th>
            <th scope="col" class="border-0" style="width: 120px;">{{ $t('notifications.dateTime') }}</th>
            <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(notification, index) of notifications"
            :key="'notification-' + index">

            <td class="align-middle">
              {{ notification.description }}
            </td>

            <td class="align-middle">
              {{ notification.date }}<br>
              {{ notification.time }}
            </td>

            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                v-if="!notification.is_completed"
                theme="success"
                :outline="true"
                @click.prevent="markAsCompleted(notification.id)"
                :disabled="markingInProgress !== false"
                :title="$t('notifications.markAsCompleted')">
                <div
                  v-if="markingInProgress === notification.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
                <i
                  v-else
                  class="material-icons">check</i>
              </d-button>
              <d-button
                v-if="notification.is_completed"
                theme="success"
                @click.prevent="markAsNotCompleted(notification.id)"
                :disabled="markingInProgress !== false"
                :title="$t('notifications.markAsNotCompleted')">
                <div
                  v-if="markingInProgress === notification.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
                <i
                  v-else
                  class="material-icons">check</i>
              </d-button>
              <d-button
                theme="primary"
                @click.prevent="editNotification(notification.id)"
                class="ml-2 mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                @click.prevent="removeNotification(notification.id)"
                :disabled="!!removeInProgress"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="!notifications || notifications.length === 0">
            <td
              colspan="6"
              class="text-center">
              {{ $t('notifications.noNotificationsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>
    </d-card>

    <contact-notification-new-popup
      v-if="showAddPopup"
      :contactType="contactType"
      :contactID="contactID" />

    <contact-notification-edit-popup
      v-if="showEditPopup"
      :notificationData="selectedNotification"/>
  </d-container>
</template>

<script>
import ContactNotificationEditPopup from './ContactNotificationEditPopup.vue';
import ContactNotificationNewPopup from './ContactNotificationNewPopup.vue';
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';

export default {
  name: 'contact-notifications',
  props: [
    'contactID',
    'contactType',
    'notifications'
  ],
  components: {
    'contact-notification-edit-popup': ContactNotificationEditPopup,
    'contact-notification-new-popup': ContactNotificationNewPopup,
  },
  computed: {
    selectedNotification () {
      let notification = this.notifications.filter(item => item.id === this.editedNotificationID);

      if (notification[0]) {
        return notification[0];
      }

      return null;
    }
  },
  data() {
    return {
      markingInProgress: false,
      removeInProgress: false,
      showAddPopup: false,
      showEditPopup: false,
      editedNotificationID: null
    };
  },
  mounted () {
    this.$bus.$on('contact-notifications-close-add-popup', this.closeAddNotificationPopup);
    this.$bus.$on('contact-notifications-close-edit-popup', this.closeEditNotificationPopup);
  },
  methods: {
    addNotification () {
      this.showAddPopup = true;
    },
    editNotification (notificationID) {
      this.editedNotificationID = notificationID;
      this.showEditPopup = true;
    },
    removeNotification (notificationID) {
      this.removeInProgress = notificationID;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/notifications/delete/',
        id: notificationID,
        successTitle: this.$t('notifications.removeNotificationSuccessTitle'),
        successText: this.$t('notifications.removeNotificationSuccessText'),
        confirmTitle: this.$t('notifications.removeNotificationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('notifications.removeNotificationErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.$bus.$emit('contact-notifications-reload-needed');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    markAsCompleted (notificationID) {
      this.markingInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/notifications/mark-completed/' + notificationID,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('notifications.markingFailedTryAgain'),
        formData: {
          is_completed: 1
        },
        successAction: () => {
          this.markingInProgress = false;
          this.$bus.$emit('contact-notifications-reload-needed');
        },
        failAction: () => {
          this.markingInProgress = false;
        }
      });
    },
    markAsNotCompleted (notificationID) {
      this.markingInProgress = notificationID;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/notifications/mark-completed/' + notificationID,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('notifications.markingFailedTryAgain'),
        formData: {
          is_completed: 0
        },
        successAction: () => {
          this.markingInProgress = false;
          this.$bus.$emit('contact-notifications-reload-needed');
        },
        failAction: () => {
          this.markingInProgress = false;
        }
      });
    },
    closeAddNotificationPopup () {
      this.showAddPopup = false;
    },
    closeEditNotificationPopup () {
      this.editedNotificationID = null;
      this.showEditPopup = false;
    }
  },
  beforeDestroy () {
    this.$bus.$off('contact-notifications-close-add-popup', this.closeAddNotificationPopup);
    this.$bus.$off('contact-notifications-close-edit-popup', this.closeEditNotificationPopup);
  }
};
</script>
